import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary } from "@/components/error-boundary";
import { Card, CardContent } from "@/components/rac/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { GallerySmall } from "@/components/sup/gallery/small";
import { PropertyInfoOverlay } from "@/components/sup/property-info-overlay";
import { useAuth } from "@/hooks/use-auth";
import { useBuyerActions } from "@/hooks/use-buyer-actions";
import { BuyerRequestContext } from "@/hooks/use-buyer-request-data";
import { useGuardedAction } from "@/hooks/use-guarded-action";
import { useBuyerPropertiesStore } from "@/stores/buyer/buyer-properties-store";
import { type MediaItem, type SellerUnitAndProject } from "@/types/sup";
import { useBuyerHelpers } from "@/utils/buyer/helpers";
import { cn } from "@/utils/cn";
import { formatRange } from "@/utils/format-range";
import { logger } from "@/utils/logger";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
// Import removed - will handle map functionality differently
import { useComparisonStoreData } from "@/hooks/global-multiple";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import dateFormat from "@/utils/date/format";
import { Bed, Building, Compass, Home, LandPlot, ParkingCircle, School, ShowerHead } from "lucide-react";
import Link from "next/link";
import React, { useCallback, useContext, useEffect } from "react";
import { ShortlistButton } from "../customer-care-widget/buttons";
import { type ComparablePrice } from "../optimization/types";
import { CompareButton } from "../search/compare/compare-button";
interface SUPData extends SellerUnitAndProject {
  comparablePrices?: ComparablePrice[];
}
export type SUPCardProps = {
  data: SUPData;
  isShortlisted?: boolean;
  onShortlist?: (sup: SellerUnitAndProject) => void;
  isMobileView?: boolean;
  shouldRedirect?: boolean;
  distanceFromText?: string;
  className?: string;
  subUnit?: boolean;
  isForPreference?: boolean;
  hideShortlistButton?: boolean;
  shouldPrioritizeCoverImage?: boolean; // Control whether to prioritize the first (cover) image
  hideGalleryInfoButton?: boolean;
  forWhat?: string;
};
const EMPTY_NUMBERS: number[] = [];
const EMPTY_STRINGS: string[] = [];
const EMPTY_MEDIA: MediaItem[] = [];
const SUPCardFallback = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center justify-center h-full"><div className="text-center"><div className="text-red-800 font-semibold mb-2">Property Card Error</div><p className="text-red-600 text-sm">Unable to load property details</p></div></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="w-full min-h-[370px] p-4 border border-red-200 rounded-lg bg-red-50 relative">{t1}{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const PROPERTY_ICONS = {
  apartment: Building,
  house: Home,
  villa: School,
  plot: LandPlot
} as const;
const PropertyIcon = t0 => {
  const $ = _c(3);
  const {
    type,
    className
  } = t0;
  const Icon = PROPERTY_ICONS[type?.toLowerCase() as keyof typeof PROPERTY_ICONS] || Building;
  let t1;
  if ($[0] !== Icon || $[1] !== className) {
    t1 = <Icon className={className} />;
    $[0] = Icon;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const shortlistButtonClassName = "w-10 h-10 border-0 transition-colors data-[hovered]:translate-y-0";
export const SUPCard = React.memo(function SUPCard({
  data,
  isShortlisted: initialShortlisted,
  onShortlist,
  isMobileView: _isMobileView = false,
  shouldRedirect = false,
  className,
  subUnit = false,
  isForPreference = false,
  hideShortlistButton = false,
  shouldPrioritizeCoverImage = false,
  // Default to false for below-the-fold content
  hideGalleryInfoButton = false,
  // Default to false
  forWhat = ""
}: SUPCardProps) {
  const sup = data;
  const {
    isAuthenticated,
    user,
    refetchUser
  } = useAuth();
  const {
    openDialog
  } = useLoginDialog();
  // Extract data first to determine unitId
  const {
    bedrooms = EMPTY_NUMBERS,
    bathrooms = EMPTY_NUMBERS,
    facings = EMPTY_STRINGS,
    localityName = "",
    projectName = "",
    owners: _owners = EMPTY_STRINGS,
    projectId = "",
    // propertyAgeInMonths = [],
    possessionStatuses = EMPTY_STRINGS,
    currentRate,
    parkings = EMPTY_NUMBERS,
    amenities = EMPTY_STRINGS,
    mediaList = EMPTY_MEDIA,
    prices,
    comparablePrices,
    builtUpAreas = EMPTY_NUMBERS,
    propertyTypes = EMPTY_STRINGS,
    type,
    _id,
    plotAreas = EMPTY_NUMBERS
  } = sup;
  const unitId = type === "unit" ? _id : null;
  const {
    handleShortlist,
    handleRemoveShortlist,
    isShortlistLoading
  } = useBuyerActions();
  const isInComparison = useComparisonStoreData(state => state.comparisonTray?.some(f => f._id === sup._id) || false);
  const toggleComparison = useComparisonStoreData(state => state.toggleComparison);
  const linkRef = React.useRef<HTMLAnchorElement>(null);

  // Dynamically import CSS only when used in preference context to reduce bundle size
  useEffect(() => {
    if (isForPreference) {
      import("@/styles/preference-cards.css");
    }
  }, [isForPreference]);

  // Get shortlist state directly from central store - using same logic as SUPTitle
  // Optimized to only re-render when the specific property's shortlist state changes
  const storeShortlistState = useBuyerPropertiesStore(state => state.isPropertyShortlisted(unitId ? sup._id : sup.projectId ?? ""));
  // Check if store has been initialized with data
  const isStoreInitialized = useBuyerPropertiesStore(state => state.lastFetchedAt !== null);

  // Derive shortlist state directly during render
  // logic: if store says yes, it's yes. if store says no but isn't initialized, check prop. if store says no and IS initialized, it's no.
  const isShortlisted = storeShortlistState || !isStoreInitialized && initialShortlisted || false;
  const {
    data: buyerUnits,
    actions
  } = useContext(BuyerRequestContext) || {};

  // const isNewListing = propertyAgeInMonths[0] <= 3;
  // Use buyer helper utilities
  const {
    getBuyerGroupId,
    createBuyerGroup
  } = useBuyerHelpers(user, refetchUser);

  // --- Shortlist Handler Logic (without guard) ---
  const performShortlistAction = useCallback(async () => {
    if (onShortlist) {
      onShortlist(data);
      return;
    }
    if (!sup._id) return;

    // Check if user is authenticated first
    if (!isAuthenticated) {
      openDialog({
        onLoginSuccess: performShortlistAction
      });
      return;
    }
    let buyerGroupId = getBuyerGroupId();
    if (!buyerGroupId) {
      buyerGroupId = await createBuyerGroup();
    }
    if (!buyerGroupId) {
      // If we still don't have a buyer group ID, prompt user to login/create account
      logger.error("Failed to get or create buyer group ID", {
        method: "handleFavoriteClick",
        unitId: sup._id,
        isAuthenticated
      });
      openDialog({
        onLoginSuccess: performShortlistAction
      });
      return;
    }
    try {
      // Create property object based on type field
      const property = sup.type === "unit" ?
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {
        __typename: "BuyerUnit",
        id: sup._id,
        unit: {
          id: sup._id
        }
      } as any :
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {
        __typename: "BuyerProject",
        id: sup.projectId ?? "",
        project: {
          id: sup.projectId ?? ""
        }
      } as any;
      if (isShortlisted) {
        await handleRemoveShortlist({
          property,
          source: "search"
        });
      } else {
        await handleShortlist(property, "search");
      }
    } catch (e) {
      logger.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sup, unitId, projectId, isShortlisted, onShortlist, data, isAuthenticated, openDialog, getBuyerGroupId, createBuyerGroup, handleShortlist, handleRemoveShortlist]);

  // Use guarded action hook
  const {
    guardedAction: handleFavoriteClick,
    GuardComponent
  } = useGuardedAction("shortlist_property", performShortlistAction);
  const isLoadingShortlist = isShortlistLoading;
  const {
    bedroomsStr,
    bathroomsStr,
    parkingsStr,
    sanitizedFacings,
    title,
    tooltipContent,
    maxBedrooms,
    maxBathrooms,
    maxParkings
  } = React.useMemo(() => {
    const bedroomsStr = formatRange({
      data: bedrooms
    });
    const bedroomsStrCompact = formatRange({
      data: bedrooms,
      withSpaces: false
    });
    const bathroomsStr = formatRange({
      data: bathrooms
    });
    const parkingsStr = formatRange({
      data: parkings
    });
    const sanitizedFacings = facings.filter(Boolean);
    const propertyType = toSentenceCase(propertyTypes?.[0] || "apartment");
    const title = subUnit ? `${bedroomsStrCompact ? `${bedroomsStrCompact}BHK` : propertyType}` : `${bedroomsStrCompact ? `${bedroomsStrCompact}BHK` : propertyType} in ${projectName || localityName || sup.locality || "Bangalore"}`;
    const tooltipContent = [...new Set(propertyTypes)].filter(Boolean).map(el => el.charAt(0).toUpperCase() + el.slice(1)).join(", ");

    // Memoize max values to avoid recalculation during render loop
    const maxBedrooms = bedrooms?.length ? Math.max(...bedrooms) : 0;
    const maxBathrooms = bathrooms?.length ? Math.max(...bathrooms) : 0;
    const maxParkings = parkings?.length ? Math.max(...parkings) : 0;
    return {
      bedroomsStr,
      bathroomsStr,
      parkingsStr,
      sanitizedFacings,
      title,
      tooltipContent,
      maxBedrooms,
      maxBathrooms,
      maxParkings
    };
  }, [bedrooms, bathrooms, parkings, facings, propertyTypes, subUnit, projectName, localityName, sup.locality]);
  let badge = "";
  if (forWhat) {
    if (forWhat === "sold_and_cancelled" && comparablePrices?.[0]?.dateOfLastPrice) {
      badge = `Sold in ${dateFormat(new Date(comparablePrices[0].dateOfLastPrice), "MMM-YYYY")}`;
    }
  } else if (possessionStatuses?.includes("under-construction") || possessionStatuses?.includes("underConstruction")) {
    badge = "Under Construction"; // Determine if property has special status
  } else if (possessionStatuses?.includes("ready-to-move-in") || possessionStatuses?.includes("readyToMoveIn")) {
    badge = "Ready to Move In"; // Determine if property has special status
  }

  // TODO: Reverted to old visit request logic using BuyerRequestContext
  const isC2cVisitRequested = buyerUnits?.projectVisitC2c?.find(({
    project: p
  }) => p.id === projectId);
  const pdValues = unitId ? buyerUnits?.buyerUnits?.find(({
    unit: u
  }) => u._id === unitId) : buyerUnits?.buyerProjects?.find(({
    project: p
  }) => p.id === projectId);
  const isVisitRequested = user && actions?.requestVisitUnits.includes(unitId ?? "") ? true : isC2cVisitRequested || pdValues?.processDetails?.[2]?.status === "requested";
  const isConfirmedVisit = pdValues?.processDetails?.[2]?.status === "confirmed";
  const linkHref = sup.type === "unit" ? `/units/${sup.slug ? `${sup.slug}-` : ""}${unitId ?? sup._id}` : sup.type === "sp" && projectId ? `/projects/${projectId}` : undefined;

  // Handle map panning when card is clicked
  const handleCardClick = useCallback((e: React.MouseEvent) => {
    // Handle redirect if needed
    if (shouldRedirect) return;else e.preventDefault();
  }, [shouldRedirect]);
  const handleMainImageClick = useCallback(() => {
    if (linkRef.current) {
      linkRef.current.click();
    }
  }, []);
  return <ErrorBoundary fallback={SUPCardFallback}>
			<Link ref={linkRef} href={linkHref || "#"} target="_blank" className={cn("w-full block", !isForPreference && !forWhat && "min-h-[370px]", isForPreference && "preference-card-scale", className)} role="button" tabIndex={0} onClick={handleCardClick}>
				<Card className={`bg-white/80 hover:bg-white h-full transition-all duration-300 group/card cursor-pointer p-0 flex flex-col gap-2 relative  shadow-none ${isForPreference ? "rounded-b-none border-b-0" : ""}`}>
					{/* Image Container */}
					<div className={`relative overflow-hidden rounded-t-xl ${isForPreference ? "aspect-[4/3] sm:aspect-[16/9] md:aspect-[16/9]" : "aspect-[4/3]"}`}>
						<GallerySmall coverImage={sup.cover || sup.appCoverImage} projectId={projectId} mediaList={mediaList} projectName={projectName || ""} type={sup.type} shouldPrioritizeCoverImage={shouldPrioritizeCoverImage} hideGalleryInfoButton={hideGalleryInfoButton} onMainImageClick={handleMainImageClick} />

						{/* Top-left badge */}
						{badge && <div className="absolute top-3 left-3">
								<span className="bg-white text-black text-sm font-medium px-3 py-1.5 rounded-full shadow-sm">
									{badge}
								</span>
							</div>}

						{/* Property info overlay */}
						{!subUnit && <PropertyInfoOverlay propertyTypes={propertyTypes} forWhat={forWhat} supType={sup.type} plotAreas={plotAreas} prices={prices} currentRate={currentRate} comparablePrices={comparablePrices} builtUpAreas={builtUpAreas} parkings={parkings} supId={sup._id} distance={sup.distance} />}
					</div>

					{/* Content */}
					<CardContent className={`px-2 flex-1 flex flex-col pb-1 ${isForPreference ? "overflow-y-auto" : ""}`}>
						{/* Main content layout */}
						<div className="mb-1 flex items-start justify-between gap-2">
							{/* Left column: Title and bedroom details */}
							<div className="flex flex-col flex-1 min-w-0">
								<h3 className="font-medium text-gray-900 text-base leading-tight line-clamp-2 mb-2">{title}</h3>
								{/* Bedroom details */}
								<div className="flex gap-4 text-gray-600 text-sm">
									{/* Property Type Icons */}
									{data.propertyTypes?.[0] && <div className="flex border-r border-gray-200 pr-3">
											<Tooltip>
												<TooltipTrigger asChild>
													<span className="h-6 w-4 flex items-center justify-start cursor-help transition-colors">
														<PropertyIcon type={data.propertyTypes[0]} className="w-4 h-4 text-gray-500" />
													</span>
												</TooltipTrigger>
												<TooltipContent side="right" align="center" avoidCollisions={true} collisionPadding={10}>
													<p className="text-sm">{tooltipContent}</p>
												</TooltipContent>
											</Tooltip>
										</div>}

									{bedroomsStr && <Tooltip>
											<TooltipTrigger asChild>
												<div className="cursor-help flex items-center">
													<Bed className="w-4 h-4 mr-1" />
													<span>{bedroomsStr}</span>
												</div>
											</TooltipTrigger>
											<TooltipContent>
												<p>
													{bedroomsStr} {" bed" + (maxBedrooms > 1 ? "s" : "")}
												</p>
											</TooltipContent>
										</Tooltip>}
									{bathroomsStr && <Tooltip>
											<TooltipTrigger asChild>
												<div className="cursor-help flex items-center">
													<ShowerHead className="w-4 h-4 mr-1" />
													<span>{bathroomsStr}</span>
												</div>
											</TooltipTrigger>
											<TooltipContent>
												<p>
													{bathroomsStr} {" bath" + (maxBathrooms > 1 ? "s" : "")}
												</p>
											</TooltipContent>
										</Tooltip>}
									{sanitizedFacings?.length > 0 && <Tooltip>
											<TooltipTrigger asChild>
												<div className="cursor-help flex items-center">
													<Compass className="w-4 h-4 mr-1" />
													<span>{sanitizedFacings.map(f => f.charAt(0).toUpperCase()).join(", ")}</span>
												</div>
											</TooltipTrigger>
											<TooltipContent>
												<p>{sanitizedFacings.map(f => f.charAt(0).toUpperCase() + f.slice(1)).join(", ")}</p>
											</TooltipContent>
										</Tooltip>}
									{parkingsStr && <Tooltip>
											<TooltipTrigger asChild>
												<div className="cursor-help flex items-center">
													<ParkingCircle className="w-4 h-4 mr-1" />
													<span>{parkingsStr}</span>
												</div>
											</TooltipTrigger>
											<TooltipContent>
												<p>
													{parkingsStr} {" car parking" + (maxParkings > 1 ? "s" : "")}
												</p>
											</TooltipContent>
										</Tooltip>}
									{amenities?.length > 0 && <Tooltip>
											<TooltipTrigger asChild>
												<div className="cursor-help flex items-center">
													<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
													</svg>
													<span>{amenities.length}</span>
												</div>
											</TooltipTrigger>
											<TooltipContent>
												<p>{amenities.length} amenities</p>
											</TooltipContent>
										</Tooltip>}
								</div>
							</div>

							{/* Right column: Shortlist and Compare buttons */}
							<div className="flex flex-col items-center gap-2">
								{/* Shortlist Button */}
								{!subUnit && !isAuthenticated && !isForPreference && !hideShortlistButton && <ShortlistButton className={shortlistButtonClassName} onPress={() => openDialog({
                onLoginSuccess: handleFavoriteClick
              })} />}
								{!subUnit && isAuthenticated && !isForPreference && !hideShortlistButton && <span title={isConfirmedVisit ? "Cannot remove shortlisting when a visit is already confirmed." : isVisitRequested ? "Cannot remove shortlisting when a visit is already requested." : ""}>
										<ShortlistButton handleShortList={handleFavoriteClick} isLoading={isLoadingShortlist} isShortlisted={isShortlisted ?? false} isDisabled={!!isVisitRequested || !!isConfirmedVisit} className={shortlistButtonClassName} />
									</span>}

								{/* Compare Button */}
								{!subUnit && !isForPreference && !hideShortlistButton && <CompareButton property={sup} isInComparison={isInComparison} onToggleComparison={toggleComparison} variant="icon" className={shortlistButtonClassName} />}
							</div>
						</div>
					</CardContent>
				</Card>
			</Link>

			{/* Relationship Guard Modal */}
			<GuardComponent />
		</ErrorBoundary>;
});